// src/lib/chatApi.js

export async function startCase(language) {
  const res = await fetch('/api/chat/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language }),
  });
  if (!res.ok) throw new Error('Could not start a conversation. Please try again.');
  return res.json(); // { caseId, caseRef }
}

/**
 * Streams a reply for the given message history. Calls onToken for each
 * text chunk, onTriage if a triage event arrives, and resolves when done.
 * Mirrors the SSE event shapes emitted by services/claude.js (Module 4).
 */
export async function sendMessage(caseId, messages, { onToken, onTriage, onHumanAck, onError }) {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseId, messages }),
  });
  if (!res.ok || !res.body) {
    onError?.('Connection lost. Please try sending that again.');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';
    for (const raw of events) {
      const line = raw.replace(/^data: /, '').trim();
      if (!line || line === '[DONE]') continue;
      try {
        const evt = JSON.parse(line);
        if (evt.type === 'token') onToken?.(evt.text ?? '');
        else if (evt.type === 'triage') onTriage?.(evt.triage);
        else if (evt.type === 'human_ack') onHumanAck?.(evt.staffName);
        else if (evt.type === 'error') onError?.(evt.message ?? 'Something went wrong.');
      } catch { /* ignore partial/non-JSON keepalive lines */ }
    }
  }
}

export async function pollForStaffMessages(caseId, afterId) {
  const res = await fetch(`/api/chat/poll?caseId=${encodeURIComponent(caseId)}&after=${afterId}`);
  if (!res.ok) return null;
  return res.json();
}

export async function saveResumePin(caseId, pin) {
  const res = await fetch('/api/chat/save-pin', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseId, pin }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? 'Could not save PIN');
  return body;
}

export async function resumeCase(caseRef, pin) {
  const res = await fetch('/api/chat/resume', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseRef, pin }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? 'Could not resume that conversation');
  return body; // { ok, caseId, messages }
}

export async function uploadAttachment(caseId, file) {
  const form = new FormData();
  form.append('caseId', caseId);
  form.append('file', file);
  const res = await fetch('/api/chat/upload', { method: 'POST', body: form });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? 'Upload failed');
  return body;
}

export function notifyTyping(caseId) {
  fetch('/api/chat/typing', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseId }),
  }).catch(() => {});
}
